import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';

// Contentful configuration
const CONTENTFUL_SPACE_ID = 'l4m1dxs9e1m1';
const CONTENTFUL_ACCESS_TOKEN = 'DdxSj8Bsf5A1t15VEaQMVy70e_n4z8fHjVBK_2oZknw';
const CONTENTFUL_API_URL = `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/master/entries`;

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPost = async () => {
            // Check if Contentful is configured
            if (CONTENTFUL_SPACE_ID === 'YOUR_SPACE_ID') {
                // Show demo content if not configured
                setPost(getDemoPost(slug));
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${CONTENTFUL_API_URL}?content_type=blogPost&fields.slug=${slug}&include=2`,
                    {
                        headers: {
                            'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
                        },
                    }
                );

                if (!response.ok) throw new Error('Failed to fetch post');

                const data = await response.json();
                if (data.items.length > 0) {
                    const item = data.items[0];
                    setPost({
                        id: item.sys.id,
                        title: item.fields.title,
                        slug: item.fields.slug,
                        body: item.fields.body,
                        publishDate: item.fields.publishDate || item.sys.createdAt,
                        featuredImage: item.fields.featuredImage 
                            ? `https:${data.includes?.Asset?.find(a => a.sys.id === item.fields.featuredImage.sys.id)?.fields.file.url}`
                            : 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80',
                        tags: item.fields.tags || [],
                    });
                }
            } catch (err) {
                console.error('Error fetching blog post:', err);
                setPost(getDemoPost(slug));
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    // Demo post when Contentful is not configured
    const getDemoPost = (slug) => {
        const demoPosts = {
            'ultimate-guide-spa-treatments': {
                id: '1',
                title: 'The Ultimate Guide to Spa Treatments',
                slug: 'ultimate-guide-spa-treatments',
                body: `## Introduction to Spa Treatments

Spa treatments have been used for centuries to promote relaxation, healing, and overall well-being. Whether you're a first-timer or a spa enthusiast, understanding the different types of treatments available can help you make the most of your spa experience.

## Popular Spa Treatments

### Swedish Massage
The most common type of massage, Swedish massage uses long, flowing strokes to promote relaxation and improve circulation. It's perfect for beginners and those looking for a gentle, soothing experience.

### Deep Tissue Massage
For those with chronic muscle tension, deep tissue massage targets the deeper layers of muscle tissue. It can help relieve severe tension and is often recommended for athletes.

### Hot Stone Therapy
Smooth, heated stones are placed on specific points of the body to relax muscles and improve blood flow. The warmth of the stones creates a deeply relaxing experience.

### Aromatherapy
Essential oils are used during massage to enhance relaxation and provide additional therapeutic benefits. Different oils offer different benefits - lavender for relaxation, eucalyptus for clarity, and more.

## Benefits of Regular Spa Visits

- Reduced stress and anxiety
- Improved sleep quality
- Relief from muscle tension and pain
- Enhanced skin health
- Better circulation
- Mental clarity and focus

## How to Choose the Right Treatment

Consider your goals when selecting a spa treatment. Are you looking to relax? Try a Swedish massage or aromatherapy. Need pain relief? Deep tissue or hot stone therapy might be better suited for you.

*Remember to communicate with your therapist about your preferences and any health concerns before your treatment begins.*`,
                publishDate: '2026-02-15',
                featuredImage: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80',
                tags: ['Wellness', 'Guide'],
            },
            'benefits-ayurvedic-massage': {
                id: '2',
                title: 'Benefits of Ayurvedic Massage',
                slug: 'benefits-ayurvedic-massage',
                body: `## What is Ayurvedic Massage?

Ayurvedic massage, also known as Abhyanga, is an ancient Indian healing practice that has been used for over 5,000 years. It involves the application of warm herbal oils to the body, combined with specific massage techniques designed to balance the body's doshas (energy types).

## The Three Doshas

In Ayurveda, there are three doshas:

1. **Vata** - Air and space energy
2. **Pitta** - Fire and water energy
3. **Kapha** - Earth and water energy

Your massage and oil selection will be customized based on your dominant dosha.

## Key Benefits

### Physical Benefits
- Nourishes the skin and tissues
- Improves circulation and lymphatic drainage
- Reduces muscle tension and joint pain
- Boosts immune function
- Promotes better sleep

### Mental Benefits
- Calms the nervous system
- Reduces anxiety and stress
- Improves mental clarity
- Balances emotions

## Popular Ayurvedic Treatments

### Shirodhara
A continuous stream of warm oil is poured onto the forehead, creating a deeply meditative state.

### Panchakarma
A comprehensive detoxification program that cleanses the body of toxins.

### Udvartana
A herbal powder massage that helps with weight management and skin rejuvenation.

*Consult with an Ayurvedic practitioner to determine the best treatment for your constitution.*`,
                publishDate: '2026-02-10',
                featuredImage: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=1200&q=80',
                tags: ['Ayurveda', 'Health'],
            },
        };

        return demoPosts[slug] || {
            id: 'default',
            title: 'Blog Post',
            slug: slug,
            body: 'This is a demo blog post. Connect Contentful to see your actual content.',
            publishDate: new Date().toISOString(),
            featuredImage: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80',
            tags: [],
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] pt-24 flex justify-center items-center">
                <div className="w-12 h-12 border-4 border-[#722F37]/20 border-t-[#722F37] rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] pt-24 flex flex-col justify-center items-center">
                <h2 className="font-heading text-2xl text-[#4A4E4D] mb-4">Post not found</h2>
                <Link to="/blog" className="btn-gold">Back to Blog</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="blog-post-page">
            <article className="container-wide mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Link */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-[#4A4E4D]/60 hover:text-[#722F37] transition-colors mb-8"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Blog
                    </Link>
                </motion.div>

                {/* Featured Image */}
                {post.featuredImage && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mb-8"
                    >
                        <img
                            src={post.featuredImage}
                            alt={post.title}
                            className="w-full max-h-[500px] object-cover rounded-2xl"
                        />
                    </motion.div>
                )}

                {/* Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="max-w-3xl mx-auto"
                >
                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#4A4E4D]/60 mb-4">
                        <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(post.publishDate).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                            })}
                        </span>
                        {Array.isArray(post.tags) && post.tags.length > 0 && (
                            <div className="flex items-center gap-2">
                                <Tag className="w-4 h-4" />
                                {post.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="px-2 py-0.5 bg-[#F5E1E4] text-[#722F37] rounded-full text-xs"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#4A4E4D] mb-8">
                        {post.title}
                    </h1>

                    {/* Body */}
                    <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-[#4A4E4D] prose-p:text-[#4A4E4D]/80 prose-a:text-[#722F37] prose-strong:text-[#4A4E4D] prose-ul:text-[#4A4E4D]/80 prose-ol:text-[#4A4E4D]/80">
                        <ReactMarkdown>{post.body}</ReactMarkdown>
                    </div>
                </motion.div>
            </article>
        </div>
    );
};

export default BlogPost;
